import { createFileRoute, Navigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { useEffect, useEffectEvent } from "react";
import { z } from "zod";

import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { TravelPurpose } from "@/features/hotels/types/types";
import { computeEffectiveDateRange } from "@/utils/computeEffectiveDateRange";

const hotelSearchSchema = z
  .object({
    // Static dates
    checkIn: z.coerce.date().optional(),
    checkOut: z.coerce.date().optional(),

    // Offset-based dates
    checkInOffset: z.coerce.number().min(0).optional(),
    stayLength: z.coerce.number().min(1).optional(),

    // Location data
    destinationId: z.coerce.string(),
    destinationName: z.string(),
    destinationCountry: z.string(),

    // Guests
    adults: fallback(z.coerce.number(), 1).default(1),

    // Other options
    travelPurpose: fallback(
      z.nativeEnum(TravelPurpose),
      TravelPurpose.LEISURE,
    ).default(TravelPurpose.LEISURE),
    utm_source: z.string().optional(),
    utm_campaign: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    try {
      getHotelDates(data);
    } catch (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: (error as Error).message,
      });

      return;
    }
  });

export type HotelSearchParams = z.infer<typeof hotelSearchSchema>;

function getHotelDates(data: HotelSearchParams) {
  const { start, end } = computeEffectiveDateRange({
    startOffset: data.checkInOffset,
    duration: data.stayLength,
    staticStart: data.checkIn,
    staticEnd: data.checkOut,
    requireEnd: true,
  });

  return { effectiveCheckIn: start, effectiveCheckOut: end! };
}

export const Route = createFileRoute("/_authenticated/hotelsExternal")({
  component: RouteComponent,
  validateSearch: zodValidator(hotelSearchSchema),
  errorComponent: () => (
    <Navigate to="/" search={{ category: "hotels" }} replace />
  ),
});

export function RouteComponent() {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const {
    destinationId,
    destinationName,
    destinationCountry,
    adults,
    travelPurpose,
  } = searchParams;

  const { updateSearchData } = useHotelSearchDataContext();

  const setSearchData = useEffectEvent(() => {
    const { effectiveCheckIn, effectiveCheckOut } = getHotelDates(searchParams);

    updateSearchData({
      destination: {
        id: destinationId,
        name: destinationName,
        countryName: destinationCountry,
        type: "CITY",
      },
      dates: {
        checkIn: effectiveCheckIn,
        checkOut: effectiveCheckOut,
      },
      rooms: [{ adults, children: 0, childrenAges: [] }],
      travelPurpose,
    });

    navigate({ to: "/hotels/list", replace: true });
  });

  useEffect(() => {
    setSearchData();
  }, []);

  return (
    <PageLoader>
      <AnimatedLoader icon="buildings" />
    </PageLoader>
  );
}
